import React from "react";
import { Box, Typography } from "@mui/material";
import styled from "styled-components";
import { motion } from "framer-motion";

const FeatureCard = styled(Box)`
  border-radius: 16px;
  padding: 32px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
  }
`;

const CardContent = styled(Box)`
  position: relative;
  z-index: 2;
  background: ${props => props.theme.palette.background.paper};
  border-radius: 12px;
  padding: 24px;
  margin-top: auto;
  text-align: center;
`;

const CardTitle = styled(Typography)`
  color: ${props => props.theme.palette.text.primary} !important;
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 24px;
  line-height: 1.2;
  padding-bottom: 16px;
`;

const CardSubtitle = styled(Typography)`
  color: ${props => props.theme.palette.text.secondary} !important;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 400;
`;

export const StyleCard = () => {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  // Array of different background images to cycle through
  const backgroundImages = [
    '/ny-orange.png',
    '/ny-green.png',
    '/ny-pink.png',
    '/ny-black.png',
    '/ny-blue.png',
    '/ny-satellite.png',
  ];

  // Autoplay image cycling
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % backgroundImages.length
      );
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [backgroundImages.length]);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      
      // Calculate normalized position (0 to 1) - only horizontal for image cycling
      const normalizedX = x / rect.width;
      
      // Calculate which background image to show based on mouse X position
      const imageIndex = Math.floor(normalizedX * backgroundImages.length);
      const clampedIndex = Math.min(Math.max(imageIndex, 0), backgroundImages.length - 1);
      
      if (clampedIndex !== currentImageIndex) {
        setCurrentImageIndex(clampedIndex);
      }
    }
  };

  return (
    <FeatureCard 
      ref={cardRef}
      onMouseMove={handleMouseMove}
    >
      <motion.div
        data-motion
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `url(${backgroundImages[currentImageIndex]})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          opacity: 0.8,
          zIndex: 1,
          borderRadius: '16px',
        }}
        key={currentImageIndex}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          opacity: {
            duration: 2,
            ease: "linear"
          }
        }}
        whileHover={{
          scale: 1.02,
        }}
      />
      <CardContent>
        <CardTitle variant="h4">
          Styling Tools
        </CardTitle>
        <CardSubtitle variant="body1">
          Choose from 12+ styles and unlimited frame and text options
        </CardSubtitle>
      </CardContent>
    </FeatureCard>
  )
};
